import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useChannelType, useCreateChannelType, useUpdateChannelType, useChannelTypes } from '@/hooks/useChannelTypes';
import type { VersionAwareProps } from '@/features/versions/types/props';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  display_order: z.number().int().min(0),
  is_default: z.boolean()
});

type FormData = z.infer<typeof formSchema>;

interface ChannelTypeFormProps extends Pick<VersionAwareProps, 'providerId' | 'versionId'> {
  typeId: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ChannelTypeForm({ 
  providerId, 
  versionId,
  typeId, 
  onSuccess, 
  onCancel 
}: ChannelTypeFormProps) {
  const { data: type, isLoading: isTypeLoading } = useChannelType(typeId);
  const { data: types } = useChannelTypes(providerId, versionId);
  const createType = useCreateChannelType();
  const updateType = useUpdateChannelType();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: type?.name || '',
      display_order: type?.display_order || 0,
      is_default: type?.is_default || false
    }
  });

  useEffect(() => {
    if (type) {
      console.log('Setting form values for type:', type);
      form.reset({
        name: type.name,
        display_order: type.display_order,
        is_default: type.is_default
      });
    }
  }, [type, form]);

  // Set initial display order for new types
  useEffect(() => {
    if (!typeId && types) {
      const maxOrder = Math.max(0, ...types.map(t => t.display_order));
      form.setValue('display_order', maxOrder + 1);
    }
  }, [typeId, types, form]);

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      
      if (typeId) {
        await updateType.mutateAsync({
          typeId,
          providerId,
          versionId,
          name: data.name,
          display_order: data.display_order,
          is_default: data.is_default
        });
      } else {
        await createType.mutateAsync({
          providerId,
          versionId,
          name: data.name,
          display_order: data.display_order,
          is_default: data.is_default
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      // Error toast is handled by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableOrders = () => {
    // For new types, next available number
    if (!typeId && types) {
      return [types.length + 1];
    }
    // For existing types, all possible positions
    return Array.from({ length: (types?.length || 0) }, (_, i) => i + 1);
  };

  if (typeId && isTypeLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="flex items-center space-x-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="flex justify-end space-x-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} disabled={isSubmitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display_order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Order</FormLabel>
              <Select
                value={field.value?.toString()}
                onValueChange={(value) => field.onChange(parseInt(value))}
                disabled={!typeId} // Disable for new types
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue>{field.value}</SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getAvailableOrders().map((order) => (
                    <SelectItem key={order} value={order.toString()}>
                      {order}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_default"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Set as Default</FormLabel>
                <FormDescription>
                  Make this the default channel type for new channels
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {typeId ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              typeId ? 'Update' : 'Create'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
